import { useQuery } from '@tanstack/react-query'
import apiClient from '@app/config/axios.config'
import { Endpoint } from '@app/config/endpoints'
import type { RiskProfileAssetsResponse } from '../types/riskProfileAssets.types'
import { useRiskProfileRequest } from './useRiskProfileRequest'
import type { RiskProfileQueryParams } from './useRiskProfileQueryParams'
import type { ProfileRiskRequest } from '../types/riskProfile.types'
import { DEFAULT_PROFILE_RISK_PAGINATION } from '../constants/riskProfile.constants'

export const RISK_PROFILE_ASSETS_QUERY_KEY = 'risk-profile-assets'

const isProfileAssetsRequest = (
  request: RiskProfileQueryParams extends never ? never : ReturnType<typeof useRiskProfileRequest>,
): request is ProfileRiskRequest => {
  if (!request) return false

  const { group_by } = request

  if (group_by === 'region') return true

  if (typeof group_by === 'object' && group_by !== null) {
    return 'hazard_group' in group_by || 'element_group' in group_by
  }

  return false
}

const fetchProfileAssets = async (
  request: ProfileRiskRequest,
): Promise<RiskProfileAssetsResponse> => {
  const { data } = await apiClient.post<RiskProfileAssetsResponse>(
    `${Endpoint.riskProfile}/assets`,
    request,
  )
  return data
}

export const useProfileAssetsQuery = (request: ProfileRiskRequest | null) => {
  return useQuery({
    queryKey: [RISK_PROFILE_ASSETS_QUERY_KEY, request],
    queryFn: () => fetchProfileAssets(request!),
    enabled:
      request !== null &&
      (request.elements === undefined || request.elements.length > 0) &&
      (request.hazards === undefined || request.hazards.length > 0),
  })
}

export function useProfileAssetsData(queryParams: RiskProfileQueryParams | null) {
  const request = useRiskProfileRequest({
    queryParams,
    // Note that we supply hazard grouping here to enable
    // risk score calcualtion in the elements API
    widget: 'riskByHazardType',
    pagination: DEFAULT_PROFILE_RISK_PAGINATION,
  })
  return useProfileAssetsQuery(isProfileAssetsRequest(request) ? request : null)
}

export function useProfileAllAssetsData(queryParams: RiskProfileQueryParams | null) {
  const request = useRiskProfileRequest({ queryParams, widget: 'riskByElementType' })
  return useProfileAssetsQuery(isProfileAssetsRequest(request) ? request : null)
}
